import { cn } from '@/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar'

const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='3'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='20,6 9,17 4,12' />
  </svg>
)

interface AvatarNameCompProps {
  src?: string
  alt?: string
  fallback?: string
  name?: string
  isName?: boolean
  size?: 'sm' | 'md' | 'lg'
  showBadge?: boolean
  showIndicator?: boolean
  indicatorColor?: 'green' | 'red' | 'yellow' | 'blue'
  className?: string
  wrapperClassName?: string
  fallbackClassName?: string
}

const sizeClasses = {
  sm: 'size-8',
  md: 'size-12',
  lg: 'size-16',
}

const indicatorSizes = {
  sm: 'size-2',
  md: 'size-3',
  lg: 'size-4',
}

const badgeSizes = {
  sm: 'size-4',
  md: 'size-5',
  lg: 'size-6',
}

const indicatorColors = {
  green: 'bg-green-500',
  red: 'bg-red-500',
  yellow: 'bg-yellow-500',
  blue: 'bg-blue-500',
}

export function AvatarNameComp({
  src,
  alt,
  fallback,
  name,
  isName = true,
  size = 'md',
  showBadge = false,
  showIndicator = false,
  indicatorColor = 'green',
  className,
  wrapperClassName,
  fallbackClassName,
}: AvatarNameCompProps) {
  // Generate fallback from name if not provided
  const avatarFallback =
    fallback ||
    (name
      ? name
          .split(' ')
          .map((n) => n[0])
          .join('')
          .toUpperCase()
          .slice(0, 2)
      : '?')

  return (
    <div
      className={cn(
        'relative inline-flex items-center gap-3',
        wrapperClassName
      )}
    >
      <div className='relative'>
        <Avatar className={cn(sizeClasses[size], className)}>
          {src && (
            <AvatarImage src={src || '/placeholder.svg'} alt={alt || name} />
          )}
          <AvatarFallback
            className={cn(
              'bg-muted text-muted-foreground font-medium',
              fallbackClassName
            )}
          >
            {avatarFallback}
          </AvatarFallback>
        </Avatar>

        {/* Verification Badge */}
        {showBadge && (
          <div
            className={cn(
              'absolute top-1 -right-1.5'
              // badgeSizes[size]
            )}
          >
            <svg
              width='20'
              height='19'
              viewBox='0 0 20 19'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g filter='url(#filter0_d_1703_26885)'>
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M11.9303 3.42852L10.9359 2.43414C10.4116 1.90981 9.56148 1.90981 9.03715 2.43414L8.04277 3.42852H6.63652C5.895 3.42852 5.29387 4.02964 5.29387 4.77116V6.17742L4.2995 7.17179C3.77517 7.69612 3.77517 8.54624 4.2995 9.07057L5.29387 10.0649V11.4712C5.29387 12.2127 5.89499 12.8138 6.63652 12.8138H8.04277L9.03715 13.8082C9.56148 14.3326 10.4116 14.3326 10.9359 13.8082L11.9303 12.8138H13.3366C14.0781 12.8138 14.6792 12.2127 14.6792 11.4712V10.0649L15.6736 9.07057C16.1979 8.54624 16.1979 7.69612 15.6736 7.17179L14.6792 6.17742V4.77116C14.6792 4.02964 14.0781 3.42852 13.3366 3.42852H11.9303ZM11.4285 4.19253C11.5124 4.27646 11.6262 4.32361 11.7449 4.32361H13.3366C13.5837 4.32361 13.7841 4.52399 13.7841 4.77116V6.3628C13.7841 6.48149 13.8313 6.59533 13.9152 6.67926L15.0406 7.80472C15.2154 7.9795 15.2154 8.26287 15.0406 8.43765L13.9152 9.5631C13.8313 9.64703 13.7841 9.76087 13.7841 9.87957V11.4712C13.7841 11.7184 13.5837 11.9187 13.3366 11.9187H11.7449C11.6262 11.9187 11.5124 11.9659 11.4285 12.0498L10.303 13.1753C10.1282 13.3501 9.84485 13.3501 9.67007 13.1753L8.54462 12.0498C8.46069 11.9659 8.34685 11.9187 8.22815 11.9187H6.63652C6.38934 11.9187 6.18897 11.7184 6.18897 11.4712V9.87957C6.18897 9.76087 6.14182 9.64703 6.05789 9.5631L4.93243 8.43765C4.75765 8.26287 4.75765 7.9795 4.93243 7.80472L6.05789 6.67926C6.14182 6.59533 6.18897 6.48149 6.18897 6.3628V4.77116C6.18897 4.52399 6.38934 4.32361 6.63652 4.32361H8.22815C8.34685 4.32361 8.46069 4.27646 8.54462 4.19253L9.67007 3.06707C9.84485 2.89229 10.1282 2.89229 10.303 3.06707L11.4285 4.19253Z'
                  fill='white'
                />
                <path
                  d='M11.7448 4.32354C11.6261 4.32354 11.5123 4.27638 11.4284 4.19245L10.3029 3.067C10.1281 2.89222 9.84478 2.89222 9.67 3.067L8.54454 4.19245C8.46061 4.27638 8.34677 4.32354 8.22808 4.32354H6.63644C6.38927 4.32354 6.18889 4.52391 6.18889 4.77108V6.36272C6.18889 6.48142 6.14174 6.59525 6.05781 6.67918L4.93235 7.80464C4.75758 7.97942 4.75757 8.26279 4.93235 8.43757L6.05781 9.56303C6.14174 9.64696 6.18889 9.76079 6.18889 9.87949V11.4711C6.18889 11.7183 6.38927 11.9187 6.63644 11.9187H8.22808C8.34677 11.9187 8.46061 11.9658 8.54454 12.0498L9.67 13.1752C9.84478 13.35 10.1281 13.35 10.3029 13.1752L11.4284 12.0498C11.5123 11.9658 11.6261 11.9187 11.7448 11.9187H13.3365C13.5837 11.9187 13.784 11.7183 13.784 11.4711V9.87949C13.784 9.76079 13.8312 9.64696 13.9151 9.56303L15.0406 8.43757C15.2153 8.26279 15.2153 7.97942 15.0406 7.80464L13.9151 6.67918C13.8312 6.59525 13.784 6.48142 13.784 6.36272V4.77108C13.784 4.52391 13.5837 4.32354 13.3365 4.32354H11.7448Z'
                  fill='#35B9E9'
                />
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M12.3916 6.94579L9.38981 9.94754L7.87988 8.43762L8.51281 7.80469L9.38981 8.68169L11.7586 6.31287L12.3916 6.94579Z'
                  fill='white'
                />
              </g>
              <defs>
                <filter
                  id='filter0_d_1703_26885'
                  x='-0.356878'
                  y='-0.630831'
                  width='20.6864'
                  height='20.6867'
                  filterUnits='userSpaceOnUse'
                  color-interpolation-filters='sRGB'
                >
                  <feFlood flood-opacity='0' result='BackgroundImageFix' />
                  <feColorMatrix
                    in='SourceAlpha'
                    type='matrix'
                    values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
                    result='hardAlpha'
                  />
                  <feOffset dy='1.59128' />
                  <feGaussianBlur stdDeviation='1.59128' />
                  <feColorMatrix
                    type='matrix'
                    values='0 0 0 0 0.105882 0 0 0 0 0.109804 0 0 0 0 0.113725 0 0 0 0.04 0'
                  />
                  <feBlend
                    mode='normal'
                    in2='BackgroundImageFix'
                    result='effect1_dropShadow_1703_26885'
                  />
                  <feBlend
                    mode='normal'
                    in='SourceGraphic'
                    in2='effect1_dropShadow_1703_26885'
                    result='shape'
                  />
                </filter>
              </defs>
            </svg>
          </div>
        )}

        {/* Online Indicator */}
        {showIndicator && (
          <div
            className={cn(
              'absolute bottom-0 right-0.5 rounded-full border-2 border-background',
              indicatorSizes[size],
              indicatorColors[indicatorColor]
            )}
          />
        )}
      </div>
      <span className='font-bold SFProRegular'>{isName ? name : ''}</span>
    </div>
  )
}
